import { useTranslation } from "react-i18next";
import { NUMBER_OFFER_MAX } from "@constants";
import EllipseIcon from "@static/img/offer/ellipse.svg";
import PhoneIcon from "@static/img/offer/phone.svg";
import WarningIcon from "@static/icons/Warning.svg";
import CheckCircleIcon from "@static/icons/CheckCircle.svg";
import { ICatalogOffer } from "../../../cart/ICart";
import { Box, Stack, SvgIcon, Typography } from "@mui/material";
import { OrderAuthorizationEnum } from "@utils/getOrderAuthorization";
import { WarningBanner } from "@common/WarningBanner/WarningBanner";
import { useStyle } from "@features/home/mobile/HomeMobileOffer/style";
import { useHomeMobileOffer } from "@features/home/mobile/HomeMobileOffer/useHomeMobileOffer";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { OfferPrice } from "@common/OfferPrice/OfferPrice";
import PlusIcon from "@static/icons/Plus.svg";
import { useDeviceType } from "@hooks/useDeviceType";
import { getOfferPrice } from "@utils/offerUtils";

interface IParams {
    offer: ICatalogOffer;
    orderAuthorization: OrderAuthorizationEnum | undefined;
    handleAddMobileOffer(selectedOffer: ICatalogOffer): void;
    hasMobileOfferInCart: boolean;
    mobileOfferCount: number;
}

export const HomeMobileOffer = (props: IParams) => {
    const { handleAddMobileOffer, offer, hasMobileOfferInCart, mobileOfferCount, orderAuthorization } = props;
    const { isMobile } = useDeviceType();

    const { classes } = useStyle();

    const { t } = useTranslation("home");

    const { onClick, cannotOrder, crossSellCannotOrder, firstUsage, otherUsages, getButtonProps } = useHomeMobileOffer({
        offer,
        orderAuthorization,
        handleAddMobileOffer,
        hasMobileOfferInCart,
    });

    const { id, label, variant } = getButtonProps();

    const shouldDisplayMobileAddedToBasket = !cannotOrder && hasMobileOfferInCart && mobileOfferCount > 0;

    return (
        <Stack className={classes.container} paddingInline={isMobile ? 2 : 0}>
            <Typography className={classes.title} variant="h1">
                {offer.description}
            </Typography>
            <Typography className={classes.subtitle} variant="large">
                {t("mobileOfferCommentPrefix")}
                <Typography className={classes.commentHighlight} component="span" variant="inherit">
                    {t("mobileOfferCommentHighlight")}
                </Typography>
            </Typography>
            <div className={classes.priceContainer}>
                <OfferPrice price={getOfferPrice(offer)} />
            </div>
            {firstUsage && (
                <Stack className={classes.firstUsage} direction="row">
                    <SvgIcon className={classes.phoneIcon} component={PhoneIcon} inheritViewBox />
                    <Typography className={classes.firstUsageDescription} color="primary">
                        {firstUsage.description}
                    </Typography>
                </Stack>
            )}
            <Stack className={classes.list}>
                {otherUsages.map((usage) => (
                    <Stack className={classes.listItem} direction="row" key={usage.description}>
                        <SvgIcon className={classes.ellipseIcon} component={EllipseIcon} inheritViewBox />
                        <Typography variant="large">{usage.description}</Typography>
                    </Stack>
                ))}
            </Stack>
            {cannotOrder && (
                <Box marginBottom={3}>
                    <WarningBanner content={t("tooMuchOffer", { max: NUMBER_OFFER_MAX })} icon={<WarningIcon />} />
                </Box>
            )}
            <div className={classes.ctaGroup}>
                <LabeledButton
                    className={classes.ctaBlock}
                    color="primary"
                    href={crossSellCannotOrder ? process.env.MYACCOUNT_URL : undefined}
                    id={id}
                    labelText={label}
                    size="large"
                    startIcon={
                        variant === "outlined" && <SvgIcon color="secondary" component={PlusIcon} inheritViewBox />
                    }
                    variant={variant}
                    onClick={crossSellCannotOrder ? undefined : onClick}
                >
                    <Typography variant="normal">{label}</Typography>
                </LabeledButton>
                {shouldDisplayMobileAddedToBasket && (
                    <div className={classes.mobileAddedToBasketWrapper}>
                        <Stack className={classes.mobileAddedToBasket} direction="row">
                            <SvgIcon className={classes.checkCircleIcon} component={CheckCircleIcon} inheritViewBox />
                            <Typography variant="small">
                                {mobileOfferCount === 1
                                    ? t("mobileAddedToBasketSingular", { count: mobileOfferCount })
                                    : t("mobileAddedToBasketPlural", { count: mobileOfferCount })}
                            </Typography>
                        </Stack>
                    </div>
                )}
            </div>
            <Typography className={classes.seeFullTerms} variant="small">
                {t("seeFullTerms")}
            </Typography>
        </Stack>
    );
};
